/**
 * 水花（积分）页面
 * 展示用户的积分余额、历史记录和统计信息
 * 黑白主题风格（Vercel 风格）
 */

import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  TrendingUp,
  TrendingDown,
  Loader2,
  Plus,
  Minus,
  Calendar,
  Award,
  Gift,
  Snowflake,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import SubsectionHeader from "@/components/shared/SubsectionHeader";
import { usePointsHistory, usePoints } from "@/hooks/usePoints";
import { PointHistory } from "@/services/pointsService";
import { SprayIcon } from "@/components/icons/SprayIcon";

/**
 * 水花 - 积分管理页面
 */
export default function Points() {
  const { t } = useTranslation();

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    error,
  } = usePointsHistory({ pageSize: 10 });

  const { data: pointsData } = usePoints();

  const historyRecords = useMemo(
    () => data?.pages.flatMap((page) => page.histories ?? []) ?? [],
    [data],
  );

  // 从第一页获取统计信息
  const firstPage = data?.pages[0];
  const statistics = firstPage?.statistics;

  // 使用独立的 usePoints钩子获取余额，确保与顶部导航栏一致
  const balance = pointsData?.balance ?? 0;
  const totalBalance = pointsData?.total_balance ?? 0;
  const frozenBalance = pointsData?.frozen_balance ?? 0;

  // 格式化积分显示
  const formatPoints = (points: number) => {
    return points >= 0 ? `+${points}` : points.toString();
  };

  // 获取操作类型图标
  const getActionIcon = (actionType: string, amount: number) => {
    if (amount > 0) {
      return <Plus className="h-4 w-4 text-foreground" />;
    } else {
      return <Minus className="h-4 w-4 text-destructive" />;
    }
  };

  // 获取操作类型徽章颜色
  const getActionBadgeColor = (amount: number) => {
    return amount > 0 ? "" : "text-destructive";
  };

  if (isLoading) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-4">
        <SubsectionHeader
          title={t("points.title")}
          icon="Droplets"
          description={t("points.description")}
          className="mb-5"
        />
        {/* 积分余额卡片骨架 */}
        <Card className="mb-6">
          <CardContent className="p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <Skeleton className="h-4 w-16 mb-2" />
                <Skeleton className="h-12 w-32" />
              </div>
              <Skeleton className="h-16 w-16 rounded-full" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Skeleton className="h-20 rounded-lg" />
              <Skeleton className="h-20 rounded-lg" />
            </div>
          </CardContent>
        </Card>
        {/* 统计卡片骨架 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-12 w-12 rounded-lg" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-6 w-12" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        {/* 积分记录骨架 */}
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-24 mb-1" />
            <Skeleton className="h-4 w-48" />
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-4 bg-muted/50 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <Skeleton className="h-10 w-10 rounded-lg" />
                    <div className="space-y-2">
                      <Skeleton className="h-5 w-32" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                  </div>
                  <div className="text-right space-y-1">
                    <Skeleton className="h-6 w-12 ml-auto" />
                    <Skeleton className="h-3 w-16 ml-auto" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-4">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              {t("points.error.loadFailed")}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-4xl px-4 py-4 sm:py-6">
      {/* 页面标题 - 居中 */}
      <SubsectionHeader
        title={t("points.title")}
        icon="Spray"
        description={t("points.description")}
        className="mb-5"
      />

      {/* 积分余额卡片 */}
      <Card className="mb-4 sm:mb-6">
        <CardContent className="p-5 sm:p-8">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <div>
              <p className="text-muted-foreground text-xs sm:text-sm mb-1">
                {t("points.balance.current")}
              </p>
              <div className="flex items-baseline gap-1.5 sm:gap-2">
                <span className="text-4xl sm:text-5xl font-bold text-foreground">
                  {balance}
                </span>
                <span className="text-lg sm:text-xl text-muted-foreground">
                  {t("points.unit")}
                </span>
              </div>
            </div>
            <SprayIcon className="h-12 w-12 sm:h-16 sm:w-16 text-muted-foreground/20" />
          </div>

          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <div className="rounded-lg border bg-muted/50 p-3 sm:p-4">
              <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                <Snowflake className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground" />
                <span className="text-xs sm:text-sm text-muted-foreground">
                  {t("points.balance.frozen")}
                </span>
              </div>
              <p className="text-xl sm:text-2xl font-bold text-foreground">
                {frozenBalance}
              </p>
            </div>
            <div className="rounded-lg border bg-muted/50 p-3 sm:p-4">
              <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                <Award className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground" />
                <span className="text-xs sm:text-sm text-muted-foreground">
                  {t("points.balance.total")}
                </span>
              </div>
              <p className="text-xl sm:text-2xl font-bold text-foreground">
                {totalBalance}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 统计卡片 */}
      {statistics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <div className="p-2 sm:p-3 bg-muted rounded-lg w-fit">
                  <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-foreground" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {t("points.statistics.totalEarned")}
                  </p>
                  <p className="text-xl sm:text-2xl font-bold text-foreground">
                    {statistics.total_earned}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <div className="p-2 sm:p-3 bg-muted rounded-lg w-fit">
                  <TrendingDown className="h-5 w-5 sm:h-6 sm:w-6 text-foreground" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {t("points.statistics.totalSpent")}
                  </p>
                  <p className="text-xl sm:text-2xl font-bold text-foreground">
                    {statistics.total_spent}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <div className="p-2 sm:p-3 bg-muted rounded-lg w-fit">
                  <Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-foreground" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {t("points.statistics.monthEarned")}
                  </p>
                  <p className="text-xl sm:text-2xl font-bold text-foreground">
                    {statistics.month_earned}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <div className="p-2 sm:p-3 bg-muted rounded-lg w-fit">
                  <Gift className="h-5 w-5 sm:h-6 sm:w-6 text-foreground" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {t("points.statistics.monthSpent")}
                  </p>
                  <p className="text-xl sm:text-2xl font-bold text-foreground">
                    {statistics.month_spent}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* 积分记录 */}
      <Card>
        <CardHeader className="px-4 sm:px-6 py-4 sm:py-6">
          <CardTitle className="text-base sm:text-lg">
            {t("points.history.title")}
          </CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            {t("points.history.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
          {historyRecords.length === 0 ? (
            <div className="py-8 sm:py-12 text-center">
              <SprayIcon className="h-12 w-12 sm:h-16 sm:w-16 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-sm sm:text-base text-muted-foreground">
                {t("points.history.empty")}
              </p>
            </div>
          ) : (
            <div className="space-y-2 sm:space-y-3">
              {historyRecords.map((history) => (
                <div
                  key={history.id}
                  className="flex items-start sm:items-center justify-between p-3 sm:p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                >
                  <div className="flex items-start sm:items-center gap-3 sm:gap-4 flex-1 min-w-0">
                    <div className="p-1.5 sm:p-2 rounded-lg bg-muted flex-shrink-0">
                      {getActionIcon(history.action_type, history.amount)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
                        <p className="font-medium text-sm sm:text-base text-foreground truncate">
                          {history.action_type_text}
                        </p>
                        <Badge
                          variant="outline"
                          className={`text-xs ${getActionBadgeColor(history.amount)}`}
                        >
                          {history.amount > 0
                            ? t("points.history.earned")
                            : t("points.history.spent")}
                        </Badge>
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        {history.formatted_date}
                      </p>
                    </div>
                  </div>
                  <div className="text-right ml-2 sm:ml-4 flex-shrink-0">
                    <p
                      className={`text-base sm:text-lg font-bold ${
                        history.amount > 0
                          ? "text-foreground"
                          : "text-destructive"
                      }`}
                    >
                      {formatPoints(history.amount)}
                    </p>
                    <p className="text-[10px] sm:text-xs text-muted-foreground">
                      {t("points.history.balanceLabel")}:{" "}
                      {history.balance_snapshot}
                    </p>
                  </div>
                </div>
              ))}

              {/* 加载更多按钮 */}
              {hasNextPage && (
                <div className="pt-3 sm:pt-4 text-center">
                  <button
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    className="inline-flex items-center gap-2 px-4 py-2 text-xs sm:text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors disabled:opacity-50"
                  >
                    {isFetchingNextPage ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {t("points.history.loading", "加载中...")}
                      </>
                    ) : (
                      t("points.history.loadMore", "加载更多")
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* 获取水花方式提示 */}
      <Card className="mt-4 sm:mt-6">
        <CardHeader className="px-4 sm:px-6 py-4 sm:py-6">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Gift className="h-4 w-4 sm:h-5 sm:w-5" />
            {t("points.howToEarn.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-muted rounded-lg flex-shrink-0">
                <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-foreground" />
              </div>
              <div>
                <h4 className="font-medium text-sm sm:text-base text-foreground mb-0.5 sm:mb-1">
                  {t("points.howToEarn.checkIn.title")}
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  {t("points.howToEarn.checkIn.description")}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-2 bg-muted rounded-lg flex-shrink-0">
                <Gift className="h-4 w-4 sm:h-5 sm:w-5 text-foreground" />
              </div>
              <div>
                <h4 className="font-medium text-sm sm:text-base text-foreground mb-0.5 sm:mb-1">
                  {t("points.howToEarn.invite.title")}
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  {t("points.howToEarn.invite.description")}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-2 bg-muted rounded-lg flex-shrink-0">
                <Award className="h-4 w-4 sm:h-5 sm:w-5 text-foreground" />
              </div>
              <div>
                <h4 className="font-medium text-sm sm:text-base text-foreground mb-0.5 sm:mb-1">
                  {t("points.howToEarn.activity.title")}
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  {t("points.howToEarn.activity.description")}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
